import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AdminLayout from '../../components/AdminLayout';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Tabs,
  Tab,
  Chip,
  Skeleton,
  Alert,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  alpha,
} from '@mui/material';
import {
  TrendingUp,
  QrCode2,
  Restaurant,
  Storefront,
  Public,
  Bolt,
  ShoppingCart,
} from '@mui/icons-material';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  Tooltip as RechartsTooltip,
} from 'recharts';
import { collection, onSnapshot, query, where, Timestamp } from '../../services/firestoreCompat';
import { db } from '../../services/firebaseConfig';

type Brand = 'saheeh' | 'pizza_brand';
type View = 'all' | Brand | 'compare';
type PeriodKey = 'today' | '7d' | '30d' | 'all';

const BRAND_COLOR: Record<Brand, string> = {
  saheeh: '#00C896',
  pizza_brand: '#e8a94b',
};
const SOURCE_COLOR: Record<'web' | 'table', string> = {
  web: '#00C896',
  table: '#e8a94b',
};
const TEXT = '#e0e0e0';
const TEXT_MUTED = '#9ca3af';
const PAPER = '#252838';
const BORDER = '#1e2139';

interface ScanDoc {
  id: string;
  brand: Brand;
  source: 'web' | 'table';
  table_number: number | null;
  session_id: string;
  ts: Date | null;
  converted: boolean;
  order_id?: string;
  order_number?: string;
  order_total?: number;
  order_type?: string;
  pathname?: string;
}

function periodStart(key: PeriodKey): Date | null {
  const now = new Date();
  if (key === 'today') {
    const d = new Date(now);
    d.setHours(0, 0, 0, 0);
    return d;
  }
  if (key === '7d') {
    const d = new Date(now);
    d.setDate(d.getDate() - 6);
    d.setHours(0, 0, 0, 0);
    return d;
  }
  if (key === '30d') {
    const d = new Date(now);
    d.setDate(d.getDate() - 29);
    d.setHours(0, 0, 0, 0);
    return d;
  }
  return null;
}

function dayKey(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

const KpiCard: React.FC<{
  label: string;
  value: string | number;
  hint?: string;
  icon: React.ReactNode;
  accent: string;
}> = ({ label, value, hint, icon, accent }) => (
  <Paper
    sx={{
      p: 2.5,
      bgcolor: PAPER,
      border: `1px solid ${BORDER}`,
      borderRadius: 2,
      height: '100%',
      position: 'relative',
      overflow: 'hidden',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        height: '100%',
        width: 4,
        bgcolor: accent,
      },
    }}
  >
    <Stack direction="row" alignItems="center" spacing={1.5}>
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: 1.5,
          bgcolor: alpha(accent, 0.14),
          color: accent,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ color: TEXT_MUTED, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600 }}>
          {label}
        </Typography>
        <Typography sx={{ color: TEXT, fontSize: '1.6rem', fontWeight: 700, lineHeight: 1.1 }}>
          {value}
        </Typography>
        {hint && (
          <Typography sx={{ color: TEXT_MUTED, fontSize: '0.7rem', mt: 0.3 }}>
            {hint}
          </Typography>
        )}
      </Box>
    </Stack>
  </Paper>
);

const ChartCard: React.FC<{
  title: string;
  subtitle?: string;
  height?: number;
  empty?: boolean;
  emptyLabel?: string;
  emptyIcon?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, subtitle, height = 280, empty, emptyLabel, emptyIcon, children }) => (
  <Paper sx={{ p: 2.5, bgcolor: PAPER, border: `1px solid ${BORDER}`, borderRadius: 2, height: '100%' }}>
    <Typography sx={{ color: TEXT, fontSize: '1rem', fontWeight: 700, mb: 0.5 }}>{title}</Typography>
    {subtitle && (
      <Typography sx={{ color: TEXT_MUTED, fontSize: '0.78rem', mb: 1.5 }}>{subtitle}</Typography>
    )}
    <Box
      sx={{
        width: '100%',
        height,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {empty ? (
        <Stack alignItems="center" spacing={1.2} sx={{ color: TEXT_MUTED }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              border: `1px dashed ${alpha('#fff', 0.18)}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: alpha('#fff', 0.4),
            }}
          >
            {emptyIcon || <QrCode2 fontSize="medium" />}
          </Box>
          <Typography sx={{ color: TEXT_MUTED, fontSize: '0.85rem', maxWidth: 240, textAlign: 'center' }}>
            {emptyLabel || 'No data yet for this period.'}
          </Typography>
        </Stack>
      ) : (
        <ResponsiveContainer width="100%" height="100%">
          {children as any}
        </ResponsiveContainer>
      )}
    </Box>
  </Paper>
);

const MarketingPage: React.FC = () => {
  const { t } = useTranslation();
  const [view, setView] = useState<View>('all');
  const [period, setPeriod] = useState<PeriodKey>('7d');
  const [scans, setScans] = useState<ScanDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Live subscribe to qr_scans (filtered by ts >= periodStart for efficiency).
  useEffect(() => {
    setLoading(true);
    setError(null);
    const start = periodStart(period);
    const col = collection(db, 'qr_scans');
    const q = start ? query(col, where('ts', '>=', Timestamp.fromDate(start))) : query(col);
    const unsub = onSnapshot(
      q,
      (snap) => {
        const rows: ScanDoc[] = snap.docs.map((d) => {
          const data = d.data() as any;
          let ts: Date | null = null;
          if (data.ts) {
            if (typeof data.ts.toDate === 'function') ts = data.ts.toDate();
            else if (data.ts.seconds) ts = new Date(data.ts.seconds * 1000);
          }
          return {
            id: d.id,
            brand: (data.brand === 'pizza_brand' ? 'pizza_brand' : 'saheeh') as Brand,
            source: data.source === 'table' ? 'table' : 'web',
            table_number: typeof data.table_number === 'number' ? data.table_number : null,
            session_id: String(data.session_id || ''),
            ts,
            converted: !!data.converted,
            order_id: data.order_id || undefined,
            order_number: data.order_number || undefined,
            order_total: typeof data.order_total === 'number' ? data.order_total : undefined,
            order_type: data.order_type || undefined,
            pathname: data.pathname || undefined,
          };
        });
        setScans(rows);
        setLoading(false);
      },
      (err) => {
        setError(err.message || 'Failed to load scans');
        setLoading(false);
      },
    );
    return () => unsub();
  }, [period]);

  // Brand-filtered subset for the active view (compare uses both, computed inline).
  const filteredScans = useMemo(() => {
    if (view === 'all' || view === 'compare') return scans;
    return scans.filter((s) => s.brand === view);
  }, [scans, view]);

  const stats = useMemo(() => {
    const total = filteredScans.length;
    const web = filteredScans.filter((s) => s.source === 'web').length;
    const table = filteredScans.filter((s) => s.source === 'table').length;
    const converted = filteredScans.filter((s) => s.converted).length;
    const revenue = filteredScans.reduce((sum, s) => sum + (s.order_total || 0), 0);
    const conversionPct = total > 0 ? (converted / total) * 100 : 0;
    return { total, web, table, converted, revenue, conversionPct };
  }, [filteredScans]);

  // Daily series for line chart (last N days).
  const dailySeries = useMemo(() => {
    const days = period === 'today' ? 1 : period === '7d' ? 7 : period === '30d' ? 30 : 60;
    const map = new Map<string, { day: string; web: number; table: number; orders: number; saheeh: number; pizza_brand: number }>();
    for (let i = days - 1; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      d.setHours(0, 0, 0, 0);
      const k = dayKey(d);
      map.set(k, { day: k.slice(5), web: 0, table: 0, orders: 0, saheeh: 0, pizza_brand: 0 });
    }
    filteredScans.forEach((s) => {
      if (!s.ts) return;
      const k = dayKey(s.ts);
      const row = map.get(k);
      if (!row) return;
      if (s.source === 'web') row.web++;
      else row.table++;
      if (s.converted) row.orders++;
      if (s.brand === 'saheeh') row.saheeh++;
      else row.pizza_brand++;
    });
    return Array.from(map.values());
  }, [filteredScans, period]);

  const sourceSplit = useMemo(
    () => [
      { name: t('marketing.webScans', 'Web (Delivery / Pickup)'), value: stats.web, color: SOURCE_COLOR.web },
      { name: t('marketing.tableScans', 'Table (Dine-in)'), value: stats.table, color: SOURCE_COLOR.table },
    ],
    [stats, t],
  );

  const tableLeaderboard = useMemo(() => {
    const counts = new Map<number, { table: number; scans: number; orders: number }>();
    filteredScans
      .filter((s) => s.source === 'table' && s.table_number != null)
      .forEach((s) => {
        const k = s.table_number as number;
        const row = counts.get(k) || { table: k, scans: 0, orders: 0 };
        row.scans++;
        if (s.converted) row.orders++;
        counts.set(k, row);
      });
    return Array.from(counts.values())
      .sort((a, b) => b.scans - a.scans)
      .slice(0, 10);
  }, [filteredScans]);

  // Compare view — per-brand totals.
  const compareData = useMemo(() => {
    const make = (b: Brand) => {
      const sub = scans.filter((s) => s.brand === b);
      const total = sub.length;
      const orders = sub.filter((x) => x.converted).length;
      const revenue = sub.reduce((sum, x) => sum + (x.order_total || 0), 0);
      const web = sub.filter((x) => x.source === 'web').length;
      const table = sub.filter((x) => x.source === 'table').length;
      return {
        brand: b,
        total,
        orders,
        revenue,
        web,
        table,
        conversion: total > 0 ? (orders / total) * 100 : 0,
      };
    };
    return [make('saheeh'), make('pizza_brand')];
  }, [scans]);

  return (
    <AdminLayout>
      <Box sx={{ p: { xs: 2, md: 3 }, color: TEXT }}>
        {/* Header */}
        <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }} justifyContent="space-between" spacing={2} sx={{ mb: 2.5 }}>
          <Box>
            <Typography sx={{ fontSize: '1.6rem', fontWeight: 800, color: TEXT, lineHeight: 1.1 }}>
              {t('marketing.title', 'Marketing & QR Tracking')}
            </Typography>
            <Typography sx={{ color: TEXT_MUTED, fontSize: '0.85rem', mt: 0.4 }}>
              {t('marketing.subtitle', 'How many people scan your QR codes — and how many become real orders.')}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1.2} flexWrap="wrap" useFlexGap>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={view}
              onChange={(_, v) => v && setView(v)}
              sx={{
                bgcolor: PAPER,
                border: `1px solid ${BORDER}`,
                '& .MuiToggleButton-root': {
                  color: TEXT_MUTED,
                  border: 'none',
                  px: 1.6,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.8rem',
                },
                '& .MuiToggleButton-root.Mui-selected': {
                  bgcolor: alpha('#00C896', 0.18),
                  color: '#fff',
                },
              }}
            >
              <ToggleButton value="all">{t('marketing.viewAll', 'Both')}</ToggleButton>
              <ToggleButton value="saheeh">Saheeh</ToggleButton>
              <ToggleButton value="pizza_brand">Pizza Brand</ToggleButton>
              <ToggleButton value="compare">{t('marketing.viewCompare', 'Compare')}</ToggleButton>
            </ToggleButtonGroup>
            <Tabs
              value={period}
              onChange={(_, v) => setPeriod(v)}
              sx={{
                minHeight: 36,
                bgcolor: PAPER,
                border: `1px solid ${BORDER}`,
                borderRadius: 1,
                '& .MuiTab-root': {
                  minHeight: 36,
                  color: TEXT_MUTED,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.8rem',
                },
                '& .Mui-selected': { color: '#fff !important' },
                '& .MuiTabs-indicator': { backgroundColor: '#00C896' },
              }}
            >
              <Tab value="today" label={t('marketing.today', 'Today')} />
              <Tab value="7d" label={t('marketing.7d', '7 days')} />
              <Tab value="30d" label={t('marketing.30d', '30 days')} />
              <Tab value="all" label={t('marketing.allTime', 'All time')} />
            </Tabs>
          </Stack>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* KPI cards */}
        <Grid container spacing={2} sx={{ mb: 2.5 }}>
          <Grid item xs={6} md={3}>
            {loading ? (
              <Skeleton variant="rounded" height={92} sx={{ bgcolor: alpha('#fff', 0.05) }} />
            ) : (
              <KpiCard
                label={t('marketing.kpiTotalScans', 'Total Scans')}
                value={stats.total.toLocaleString()}
                hint={t('marketing.kpiTotalScansHint', 'Visits via QR / direct link')}
                icon={<QrCode2 fontSize="small" />}
                accent="#00C896"
              />
            )}
          </Grid>
          <Grid item xs={6} md={3}>
            {loading ? (
              <Skeleton variant="rounded" height={92} sx={{ bgcolor: alpha('#fff', 0.05) }} />
            ) : (
              <KpiCard
                label={t('marketing.kpiWebScans', 'Web (Delivery/Pickup)')}
                value={stats.web.toLocaleString()}
                hint={t('marketing.kpiWebHint', 'Marketing / flyer / direct')}
                icon={<Public fontSize="small" />}
                accent="#00C896"
              />
            )}
          </Grid>
          <Grid item xs={6} md={3}>
            {loading ? (
              <Skeleton variant="rounded" height={92} sx={{ bgcolor: alpha('#fff', 0.05) }} />
            ) : (
              <KpiCard
                label={t('marketing.kpiTableScans', 'Table (Dine-in)')}
                value={stats.table.toLocaleString()}
                hint={t('marketing.kpiTableHint', 'Customers in the restaurant')}
                icon={<Restaurant fontSize="small" />}
                accent="#e8a94b"
              />
            )}
          </Grid>
          <Grid item xs={6} md={3}>
            {loading ? (
              <Skeleton variant="rounded" height={92} sx={{ bgcolor: alpha('#fff', 0.05) }} />
            ) : (
              <KpiCard
                label={t('marketing.kpiConversion', 'Conversion')}
                value={`${stats.conversionPct.toFixed(1)}%`}
                hint={`${stats.converted.toLocaleString()} ${t('marketing.ordersFromScans', 'orders from scans')} · €${stats.revenue.toFixed(2)}`}
                icon={<TrendingUp fontSize="small" />}
                accent="#00C896"
              />
            )}
          </Grid>
        </Grid>

        {/* Compare view */}
        {view === 'compare' ? (
          <Grid container spacing={2}>
            {compareData.map((row) => (
              <Grid item xs={12} md={6} key={row.brand}>
                <Paper sx={{ p: 2.5, bgcolor: PAPER, border: `1px solid ${BORDER}`, borderRadius: 2 }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: BRAND_COLOR[row.brand] }} />
                    <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', color: TEXT }}>
                      {row.brand === 'saheeh' ? 'Saheeh' : 'Pizza Pizza Brand'}
                    </Typography>
                  </Stack>
                  <Grid container spacing={1.5}>
                    <Grid item xs={6}>
                      <Typography sx={{ fontSize: '0.72rem', color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                        {t('marketing.scans', 'Scans')}
                      </Typography>
                      <Typography sx={{ fontSize: '1.4rem', fontWeight: 700, color: TEXT }}>
                        {row.total.toLocaleString()}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography sx={{ fontSize: '0.72rem', color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                        {t('marketing.orders', 'Orders')}
                      </Typography>
                      <Typography sx={{ fontSize: '1.4rem', fontWeight: 700, color: TEXT }}>
                        {row.orders.toLocaleString()}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography sx={{ fontSize: '0.72rem', color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                        {t('marketing.conversion', 'Conversion')}
                      </Typography>
                      <Typography sx={{ fontSize: '1.4rem', fontWeight: 700, color: BRAND_COLOR[row.brand] }}>
                        {row.conversion.toFixed(1)}%
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography sx={{ fontSize: '0.72rem', color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                        {t('marketing.revenue', 'Revenue')}
                      </Typography>
                      <Typography sx={{ fontSize: '1.4rem', fontWeight: 700, color: TEXT }}>
                        €{row.revenue.toFixed(2)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                        <Chip
                          size="small"
                          icon={<Public sx={{ fontSize: 14 }} />}
                          label={`${t('marketing.web', 'Web')} ${row.web}`}
                          sx={{ bgcolor: alpha(SOURCE_COLOR.web, 0.16), color: SOURCE_COLOR.web, fontWeight: 600 }}
                        />
                        <Chip
                          size="small"
                          icon={<Restaurant sx={{ fontSize: 14 }} />}
                          label={`${t('marketing.dineIn', 'Dine-in')} ${row.table}`}
                          sx={{ bgcolor: alpha(SOURCE_COLOR.table, 0.16), color: SOURCE_COLOR.table, fontWeight: 600 }}
                        />
                      </Stack>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            ))}
            <Grid item xs={12}>
              <ChartCard
                title={t('marketing.compareDailyTitle', 'Daily scans — Saheeh vs Pizza Brand')}
                subtitle={t('marketing.compareDailySub', 'Side-by-side scan volume per brand over time')}
                height={300}
                empty={scans.length === 0}
                emptyLabel={t('marketing.emptyCompare', 'No scans yet to compare.') as string}
                emptyIcon={<TrendingUp />}
              >
                <LineChart data={dailySeries} margin={{ top: 8, right: 16, bottom: 0, left: -10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={alpha('#fff', 0.08)} />
                  <XAxis dataKey="day" stroke={TEXT_MUTED} tick={{ fontSize: 11 }} />
                  <YAxis stroke={TEXT_MUTED} tick={{ fontSize: 11 }} allowDecimals={false} />
                  <RechartsTooltip
                    contentStyle={{ background: PAPER, border: `1px solid ${BORDER}`, color: TEXT }}
                  />
                  <Legend wrapperStyle={{ color: TEXT_MUTED, fontSize: 12 }} />
                  <Line type="monotone" dataKey="saheeh" name="Saheeh" stroke={BRAND_COLOR.saheeh} strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                  <Line type="monotone" dataKey="pizza_brand" name="Pizza Brand" stroke={BRAND_COLOR.pizza_brand} strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                </LineChart>
              </ChartCard>
            </Grid>
          </Grid>
        ) : (
          /* Default view (all / per-brand) */
          <Grid container spacing={2}>
            <Grid item xs={12} md={8}>
              <ChartCard
                title={t('marketing.dailyTitle', 'Scans vs Orders')}
                subtitle={t('marketing.dailySub', 'Daily web scans, dine-in scans, and orders that came from a scan')}
                height={300}
                empty={stats.total === 0}
                emptyLabel={t('marketing.emptyDaily', 'Once visitors start scanning your QR code, daily scans and orders will appear here.') as string}
                emptyIcon={<TrendingUp />}
              >
                <LineChart data={dailySeries} margin={{ top: 8, right: 16, bottom: 0, left: -10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={alpha('#fff', 0.08)} />
                  <XAxis dataKey="day" stroke={TEXT_MUTED} tick={{ fontSize: 11 }} />
                  <YAxis stroke={TEXT_MUTED} tick={{ fontSize: 11 }} allowDecimals={false} />
                  <RechartsTooltip
                    contentStyle={{ background: PAPER, border: `1px solid ${BORDER}`, color: TEXT }}
                  />
                  <Legend wrapperStyle={{ color: TEXT_MUTED, fontSize: 12 }} />
                  <Line type="monotone" dataKey="web" name={t('marketing.webScans', 'Web') as string} stroke={SOURCE_COLOR.web} strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                  <Line type="monotone" dataKey="table" name={t('marketing.tableScans', 'Dine-in') as string} stroke={SOURCE_COLOR.table} strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                  <Line type="monotone" dataKey="orders" name={t('marketing.orders', 'Orders') as string} stroke="#fff" strokeWidth={2} strokeDasharray="4 3" dot={false} />
                </LineChart>
              </ChartCard>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 2.5, bgcolor: PAPER, border: `1px solid ${BORDER}`, borderRadius: 2, height: '100%' }}>
                <Typography sx={{ color: TEXT, fontSize: '1rem', fontWeight: 700, mb: 0.5 }}>
                  {t('marketing.sourceSplitTitle', 'Where scans come from')}
                </Typography>
                <Typography sx={{ color: TEXT_MUTED, fontSize: '0.78rem', mb: 1 }}>
                  {t('marketing.sourceSplitSub', 'Web (delivery/pickup) vs in-restaurant table QR')}
                </Typography>
                {stats.total === 0 ? (
                  <Box sx={{ height: 240, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Stack alignItems="center" spacing={1.2}>
                      <Box
                        sx={{
                          width: 56, height: 56, borderRadius: '50%',
                          border: `1px dashed ${alpha('#fff', 0.18)}`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          color: alpha('#fff', 0.4),
                        }}
                      >
                        <QrCode2 />
                      </Box>
                      <Typography sx={{ color: TEXT_MUTED, fontSize: '0.85rem', textAlign: 'center', maxWidth: 200 }}>
                        {t('marketing.emptySource', 'No scans yet for this period.')}
                      </Typography>
                    </Stack>
                  </Box>
                ) : (
                  <Box sx={{ position: 'relative', height: 240 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={sourceSplit}
                          dataKey="value"
                          nameKey="name"
                          innerRadius={58}
                          outerRadius={84}
                          paddingAngle={3}
                          stroke="none"
                        >
                          {sourceSplit.map((entry, idx) => (
                            <Cell key={idx} fill={entry.color} />
                          ))}
                        </Pie>
                        <RechartsTooltip
                          contentStyle={{ background: PAPER, border: `1px solid ${BORDER}`, color: TEXT }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                    {/* Center total */}
                    <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                      <Typography sx={{ fontSize: '1.6rem', fontWeight: 800, color: TEXT, lineHeight: 1 }}>
                        {stats.total.toLocaleString()}
                      </Typography>
                      <Typography sx={{ fontSize: '0.7rem', color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: '0.1em', mt: 0.4 }}>
                        {t('marketing.scans', 'Scans')}
                      </Typography>
                    </Box>
                  </Box>
                )}
                <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 1.5 }}>
                  {sourceSplit.map((entry) => (
                    <Stack key={entry.name} direction="row" alignItems="center" spacing={0.8}>
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: entry.color }} />
                      <Typography sx={{ fontSize: '0.78rem', color: TEXT_MUTED }}>
                        {entry.name} <span style={{ color: TEXT, fontWeight: 700 }}>{entry.value}</span>
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <ChartCard
                title={t('marketing.tablesTitle', 'Top tables (dine-in scans)')}
                subtitle={t('marketing.tablesSub', 'Which tables drive the most QR scans')}
                height={300}
                empty={tableLeaderboard.length === 0}
                emptyLabel={t('marketing.emptyTables', 'No dine-in scans yet — when guests scan a table QR, the busiest tables will rank here.') as string}
                emptyIcon={<Restaurant />}
              >
                <BarChart data={tableLeaderboard} margin={{ top: 8, right: 16, bottom: 0, left: -10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={alpha('#fff', 0.08)} />
                  <XAxis dataKey="table" stroke={TEXT_MUTED} tick={{ fontSize: 11 }} tickFormatter={(v) => `#${v}`} />
                  <YAxis stroke={TEXT_MUTED} tick={{ fontSize: 11 }} allowDecimals={false} />
                  <RechartsTooltip
                    contentStyle={{ background: PAPER, border: `1px solid ${BORDER}`, color: TEXT }}
                    labelFormatter={(v) => `${t('marketing.table', 'Table')} ${v}`}
                  />
                  <Legend wrapperStyle={{ color: TEXT_MUTED, fontSize: 12 }} />
                  <Bar dataKey="scans" name={t('marketing.scans', 'Scans') as string} fill={SOURCE_COLOR.table} radius={[4, 4, 0, 0]} />
                  <Bar dataKey="orders" name={t('marketing.orders', 'Orders') as string} fill={SOURCE_COLOR.web} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ChartCard>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2.5, bgcolor: PAPER, border: `1px solid ${BORDER}`, borderRadius: 2, height: '100%' }}>
                <Typography sx={{ color: TEXT, fontSize: '1rem', fontWeight: 700, mb: 0.5 }}>
                  {t('marketing.conversionTitle', 'Conversion breakdown')}
                </Typography>
                <Typography sx={{ color: TEXT_MUTED, fontSize: '0.78rem', mb: 2 }}>
                  {t('marketing.conversionSub', 'Of the people who scan, how many actually order')}
                </Typography>

                {(() => {
                  const webOrders = filteredScans.filter((s) => s.source === 'web' && s.converted).length;
                  const tableOrders = filteredScans.filter((s) => s.source === 'table' && s.converted).length;
                  const webPct = stats.web > 0 ? (webOrders / stats.web) * 100 : 0;
                  const tablePct = stats.table > 0 ? (tableOrders / stats.table) * 100 : 0;
                  const rows = [
                    {
                      label: t('marketing.webChannel', 'Web (Delivery / Pickup)'),
                      icon: <Public sx={{ fontSize: 18 }} />,
                      scans: stats.web,
                      orders: webOrders,
                      pct: webPct,
                      color: SOURCE_COLOR.web,
                    },
                    {
                      label: t('marketing.tableChannel', 'Table (Dine-in)'),
                      icon: <Restaurant sx={{ fontSize: 18 }} />,
                      scans: stats.table,
                      orders: tableOrders,
                      pct: tablePct,
                      color: SOURCE_COLOR.table,
                    },
                  ];
                  return rows.map((row, idx) => (
                    <Box key={idx} sx={{ mb: 2 }}>
                      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.6 }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Box sx={{ color: row.color }}>{row.icon}</Box>
                          <Typography sx={{ color: TEXT, fontSize: '0.88rem', fontWeight: 600 }}>{row.label}</Typography>
                        </Stack>
                        <Typography sx={{ color: row.color, fontSize: '1rem', fontWeight: 800 }}>
                          {row.pct.toFixed(1)}%
                        </Typography>
                      </Stack>
                      <Box sx={{ width: '100%', height: 8, bgcolor: alpha('#fff', 0.05), borderRadius: 4, overflow: 'hidden' }}>
                        <Box sx={{ width: `${Math.min(100, row.pct)}%`, height: '100%', bgcolor: row.color, transition: 'width .4s' }} />
                      </Box>
                      <Typography sx={{ color: TEXT_MUTED, fontSize: '0.74rem', mt: 0.6 }}>
                        {row.orders.toLocaleString()} {t('marketing.ordersFromScans', 'orders from scans')} / {row.scans.toLocaleString()} {t('marketing.scansLower', 'scans')}
                      </Typography>
                    </Box>
                  ));
                })()}

                <Box sx={{ mt: 1, pt: 2, borderTop: `1px solid ${BORDER}` }}>
                  <Stack direction="row" alignItems="center" spacing={1.2}>
                    <Bolt sx={{ color: stats.conversionPct > 10 ? '#00C896' : stats.conversionPct > 0 ? '#e8a94b' : TEXT_MUTED, fontSize: 18 }} />
                    <Typography sx={{ color: TEXT_MUTED, fontSize: '0.78rem', flex: 1 }}>
                      {stats.total === 0
                        ? t('marketing.insightEmpty', 'No scans tracked yet for this period.')
                        : stats.conversionPct === 0
                          ? t('marketing.insightZeroConv', 'Visitors arrive but no orders yet — check menu/UX or wait for first orders to come in.')
                          : stats.conversionPct > 10
                            ? t('marketing.insightStrong', 'Strong conversion — your QR campaigns are working.')
                            : t('marketing.insightWeak', 'Conversion below 10% — consider clearer calls-to-action on your printed materials.')}
                    </Typography>
                  </Stack>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        )}

        {/* Footer hint */}
        {!loading && stats.total === 0 && (
          <Paper sx={{ p: 3, mt: 2.5, bgcolor: PAPER, border: `1px dashed ${BORDER}`, borderRadius: 2, textAlign: 'center' }}>
            <Stack alignItems="center" spacing={1}>
              <ShoppingCart sx={{ color: TEXT_MUTED, fontSize: 32 }} />
              <Typography sx={{ color: TEXT, fontWeight: 700 }}>
                {t('marketing.emptyTitle', 'No scans yet')}
              </Typography>
              <Typography sx={{ color: TEXT_MUTED, fontSize: '0.85rem', maxWidth: 480 }}>
                {t(
                  'marketing.emptySub',
                  'As soon as customers start scanning printed QR codes or table cards, you\'ll see live stats here.',
                )}
              </Typography>
              <Stack direction="row" spacing={1.2} sx={{ mt: 1 }}>
                <Chip icon={<Storefront />} label="saheehpos.com" sx={{ bgcolor: alpha(BRAND_COLOR.saheeh, 0.16), color: BRAND_COLOR.saheeh, fontWeight: 600 }} />
                <Chip icon={<Storefront />} label="pizza_brand-huerth.de" sx={{ bgcolor: alpha(BRAND_COLOR.pizza_brand, 0.16), color: BRAND_COLOR.pizza_brand, fontWeight: 600 }} />
              </Stack>
            </Stack>
          </Paper>
        )}
      </Box>
    </AdminLayout>
  );
};

export default MarketingPage;
